import { Link, useParams } from "react-router-dom";
import {
 ArrowUpRight,
 ArrowLeft,
 Check,
 Phone,
 ChevronRight,
 ShieldCheck,
 BadgeCheck,
 Users,
 MapPin,
 Wrench,
 FileCheck,
 Leaf,
} from "lucide-react";
import { SERVICE_DETAILS, SERVICES, BUSINESS } from "@/data/content";
import { CITIES, CITY_ORDER } from "@/data/cities";
import { PANEL_CITIES, PANEL_CITY_ORDER } from "@/data/panelUpgradeCities";
import { EMERGENCY_CITIES, EMERGENCY_CITY_ORDER } from "@/data/emergencyCities";
import { SECURITY_CITIES, SECURITY_CITY_ORDER } from "@/data/securityCities";
import { GENERAL_CITIES, GENERAL_CITY_ORDER } from "@/data/generalCities";
import { COMMERCIAL_CITIES, COMMERCIAL_CITY_ORDER } from "@/data/commercialCities";
import { LIGHTING_CITIES, LIGHTING_CITY_ORDER } from "@/data/lightingCities";
import useSEO from "@/hooks/useSEO";
import { faqSchema, serviceSchema, breadcrumbSchema } from "@/lib/schema";
import { SERVICE_SEO } from "@/data/serviceSeo";

const SERVICE_LOCAL = {
 "ev-charger-installation": { cities: CITIES, order: CITY_ORDER, label: "EV Charger", guideHref: "/guides/bc-ev-charger-rebates-2026", guideTitle: "2026 BC EV Charger Rebates Guide", guideBody: "How much CleanBC and BC Hydro actually pay in 2026 — with permit rules, cost bands, and the step-by-step to claim it." },
 "panel-upgrades": { cities: PANEL_CITIES, order: PANEL_CITY_ORDER, label: "Panel Upgrade", guideHref: "/guides/electrical-panel-upgrade-cost-bc-2026", guideTitle: "2026 BC Panel Upgrade Cost Guide", guideBody: "What a 200-amp panel upgrade actually costs in BC in 2026, the permit process, and what Lower Mainland homeowners should know before booking." },
 emergency: { cities: EMERGENCY_CITIES, order: EMERGENCY_CITY_ORDER, label: "Emergency Electrician" },
 security: { cities: SECURITY_CITIES, order: SECURITY_CITY_ORDER, label: "Security & Automation" },
 "general-electrical": { cities: GENERAL_CITIES, order: GENERAL_CITY_ORDER, label: "General Electrical", blurb: "Our licensed electricians run daily service calls across the Lower Mainland — repairs, outlets, fixtures, and panel checks. Pick your city for a page written specifically for your area." },
 "commercial-wiring": { cities: COMMERCIAL_CITIES, order: COMMERCIAL_CITY_ORDER, label: "Commercial Wiring", blurb: "From retail fit-outs in Vancouver to warehouse power in Delta — our commercial crews work across the Lower Mainland. Pick your city for a page written for your area." },
 lighting: { cities: LIGHTING_CITIES, order: LIGHTING_CITY_ORDER, label: "Lighting", blurb: "From condo slim-LED retrofits in Burnaby to full landscape designs in South Surrey — pick your city for a page written for its homes." },
};

const WHY_ICONS = [ShieldCheck, BadgeCheck, Users, MapPin, Wrench, FileCheck];

export default function ServiceDetail() {
 const { slug } = useParams();
 const s = SERVICE_DETAILS[slug];

 const jsonld = s
 ? [
 serviceSchema({
 name: s.title,
 description: s.lead,
 url: `/services/${slug}`,
 image: s.heroImage,
 serviceType: s.title,
 offersUrl: `/services/${slug}`,
 }),
 breadcrumbSchema([
 { name: "Home", href: "/" },
 { name: "Services", href: "/#services" },
 { name: s.title, href: `/services/${slug}` },
 ]),
 faqSchema(s.faqs),
 ].filter(Boolean)
 : [];

 const seoOverride = SERVICE_SEO[slug] || {};
 useSEO({
 title: s ? (seoOverride.title || `${s.title} in the Lower Mainland | ElectroMax Systems`) : "Service | ElectroMax Systems",
 description: s ? (seoOverride.description || (s.lead || "").slice(0, 160)) : "Licensed BC electrical services across Metro Vancouver.",
 canonical: s ? undefined : undefined,
 image: s?.heroImage,
 type: "service",
 jsonld,
 });

 if (!s) {
 return (
 <section className="pt-40 pb-32 min-h-[70vh] bg-[#0A0A0A]">
 <div className="mx-auto max-w-[900px] px-6 text-center">
 <div className="text-[10px] tracking-[0.24em] uppercase text-white/50 mb-3">
 Service not found
 </div>
 <h1 className="font-display text-5xl font-black tracking-tight">
 That page has been re-wired.
 </h1>
 <p className="mt-4 text-white/70">
 The service you&rsquo;re looking for isn&rsquo;t here. Browse all services below.
 </p>
 <Link
 to="/#services"
 className="inline-flex mt-8 items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold px-6 py-3"
 >
 View all services <ArrowUpRight className="w-4 h-4" />
 </Link>
 </div>
 </section>
 );
 }

 return (
 <div data-testid="service-detail-page">
 {/* Hero */}
 <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden grain">
 <div className="pointer-events-none absolute -top-40 -right-40 w-[560px] h-[560px] bg-[#E60000]/10 blur-[140px] rounded-full" />
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
 <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/50 mb-8">
 <Link to="/" className="hover:text-white">Home</Link>
 <ChevronRight className="w-3 h-3" />
 <Link to="/#services" className="hover:text-white">Services</Link>
 <ChevronRight className="w-3 h-3" />
 <span className="text-white">{s.title}</span>
 </div>

 <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-end">
 <div className="lg:col-span-7 em-rise">
 <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4">
 {s.kicker}
 </div>
 <h1 className="font-display text-5xl sm:text-6xl lg:text-[76px] leading-[0.95] tracking-tighter font-black">
 {s.h1Prefix} <span className="text-white/40">{s.h1Accent}</span>
 </h1>
 <p className="mt-6 max-w-xl text-base md:text-lg text-white/70 leading-relaxed">
 {s.lead}
 </p>
 <div className="mt-8 flex flex-wrap gap-3">
 <Link
 to="/#contact"
 data-testid="service-quote-cta"
 className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold tracking-wide px-7 py-4 text-sm transition-colors"
 >
 Schedule Installation Today
 <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
 </Link>
 <a
 href={BUSINESS.phoneHref}
 className="inline-flex items-center gap-2 bg-transparent text-white border border-[#262626] hover:border-white transition-colors font-bold tracking-wide px-7 py-4 text-sm"
 >
 <Phone className="w-4 h-4" strokeWidth={2.5} />
 {BUSINESS.phone}
 </a>
 </div>
 </div>
 <div className="lg:col-span-5 em-rise" style={{ animationDelay: "120ms" }}>
 <div className="relative border border-[#262626]">
 <img src={s.heroImage} alt={`${s.title} across Metro Vancouver — ElectroMax Systems`} loading="eager" fetchPriority="high" className="w-full h-[420px] md:h-[540px] object-cover" />
 <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/40 to-transparent" />
 <div className="absolute -top-3 -left-3 hidden md:block bg-[#E60000] text-white text-[10px] tracking-[0.24em] uppercase font-bold px-3 py-2">
 BC Licensed · Insured
 </div>
 </div>
 </div>
 </div>
 </div>
 </section>

 {/* What we cover */}
 <section className="py-24 lg:py-32 bg-[#F4F4F5] text-[#0A0A0A] border-t border-[#E4E4E7]">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
 <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
 <div className="md:col-span-7">
 <div className="text-xs tracking-[0.24em] uppercase text-black/50 mb-4">
 {s.coveredKicker || "— What's Included"}
 </div>
 <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
 {s.coveredHeadingA || "What we cover"}
 <br />
 <span className="text-black/40">{s.coveredHeadingB || "on every install."}</span>
 </h2>
 </div>
 <p className="md:col-span-5 text-black/65 leading-relaxed">
 {s.coveredIntro || "When you book with us you get the full job done — not just the charger on the wall. Here's everything we take care of at your home."}
 </p>
 </div>

 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E4E4E7] border border-[#E4E4E7]">
 {s.covered.map((c, i) => (
 <div key={c.h} className="bg-white p-6 md:p-8 flex flex-col gap-4">
 <div className="flex items-baseline gap-3">
 <span className="font-display text-4xl font-black text-[#E60000]/80">
 {String(i + 1).padStart(2, "0")}
 </span>
 <span className="inline-flex w-6 h-6 bg-[#E60000] items-center justify-center">
 <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
 </span>
 </div>
 <h3 className="font-display text-xl md:text-2xl font-bold leading-tight text-black">
 {c.h}
 </h3>
 <p className="text-sm md:text-base text-black/70 leading-relaxed">{c.p}</p>
 </div>
 ))}
 </div>
 </div>
 </section>

 {/* Process */}
 <section className="py-24 lg:py-32 border-t border-[#262626]">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
 <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
 <div className="md:col-span-7">
 <div className="text-xs tracking-[0.24em] uppercase text-white/60 mb-4">
 {s.processKicker || "— Step-by-Step Process"}
 </div>
 <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
 {s.processHeadingA || "From first call"}
 <br />
 <span className="text-white/40">{s.processHeadingB || "to your first charge."}</span>
 </h2>
 </div>
 <p className="md:col-span-5 text-white/70 leading-relaxed">
 {s.processIntro || "Getting an EV charger installed at your home doesn\u2019t have to be complicated. Here\u2019s exactly what we do — from the moment you call us to the moment your charger is ready to use."}
 </p>
 </div>

 <ol className="relative border-l border-[#262626] ml-4">
 {s.process.map((p) => (
 <li key={p.n} className="pl-8 md:pl-12 pb-10 last:pb-0 relative">
 <span className="absolute left-0 -translate-x-1/2 top-1 w-3 h-3 bg-[#E60000]" />
 <div className="flex items-baseline gap-3">
 <span className="font-display text-3xl md:text-4xl font-black text-[#E60000]/80">{p.n}</span>
 <span className="text-[10px] tracking-[0.24em] uppercase text-white/50">Step</span>
 </div>
 <h3 className="mt-2 font-display text-xl md:text-2xl font-bold">{p.h}</h3>
 <p className="mt-2 text-white/70 max-w-2xl leading-relaxed">{p.p}</p>
 </li>
 ))}
 </ol>
 </div>
 </section>

 {/* Why it matters */}
 <section className="py-24 lg:py-32 border-t border-[#262626] bg-[#0A0A0A]">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12">
 <div className="lg:col-span-5">
 <div className="text-xs tracking-[0.24em] uppercase text-white/60 mb-4">
 {s.mattersKicker || "— Why It Matters"}
 </div>
 <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
 {s.mattersHeadingA || "Home charging is"}
 <br />
 <span className="text-[#E60000]">{s.mattersHeadingB || "the whole game."}</span>
 </h2>
 </div>
 <div className="lg:col-span-7 space-y-6">
 {s.matters.map((p, i) => (
 <p key={i} className="text-base md:text-lg text-white/75 leading-relaxed">
 {p}
 </p>
 ))}
 </div>
 </div>
 </section>

 {/* Rebates */}
 {!s.hideRebates && (
 <section className="py-24 lg:py-32 border-t border-[#E4E4E7] bg-[#F4F4F5] text-[#0A0A0A]">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
 <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
 <div className="md:col-span-7">
 <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4 inline-flex items-center gap-2">
 <Leaf className="w-3.5 h-3.5" strokeWidth={2.5} /> Rebates Available
 </div>
 <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
 Get money back
 <br />
 <span className="text-black/40">for your install.</span>
 </h2>
 </div>
 <p className="md:col-span-5 text-black/65 leading-relaxed">
 If you&rsquo;re getting an EV charger installed at your home, there&rsquo;s a good chance you qualify for a rebate. Here&rsquo;s exactly what&rsquo;s available and how much you can get back.
 </p>
 </div>

 <div className="grid md:grid-cols-3 gap-4 md:gap-6">
 {s.rebates.map((r, i) => (
 <div
 key={r.title}
 className={`border border-[#E4E4E7] bg-white p-6 md:p-8 flex flex-col ${
 i === 0 ? "md:col-span-3 lg:col-span-1 lg:row-span-1" : ""
 }`}
 >
 <div className="flex items-baseline gap-3">
 <span className="font-display text-4xl font-black text-[#E60000]/80">
 {String(i + 1).padStart(2, "0")}
 </span>
 <span className="text-[10px] tracking-[0.24em] uppercase text-black/45">Program</span>
 </div>
 <h3 className="mt-4 font-display text-xl md:text-2xl font-bold text-black leading-tight">
 {r.title}
 </h3>
 <p className="mt-3 text-sm md:text-base text-black/70 leading-relaxed">{r.body}</p>
 {r.rows && (
 <ul className="mt-5 pt-4 border-t border-[#E4E4E7] space-y-2">
 {r.rows.map((row) => (
 <li key={row.k} className="flex justify-between gap-4 text-sm">
 <span className="text-black/60">{row.k}</span>
 <span className="font-semibold text-black text-right">{row.v}</span>
 </li>
 ))}
 </ul>
 )}
 </div>
 ))}
 </div>
 </div>
 </section>
 )}

 {/* Pricing strip */}
 {!s.hidePricing && (
 <section className="border-y border-[#262626] bg-[#141414]">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-12 md:py-16 grid md:grid-cols-12 gap-8 items-center">
 <div className="md:col-span-7">
 <div className="text-[10px] tracking-[0.24em] uppercase text-white/55 mb-2">Typical Install</div>
 <div className="font-display text-5xl md:text-6xl font-black tracking-tighter">
 {s.priceFrom} <span className="text-white/40 text-2xl md:text-3xl align-middle">/ all-in</span>
 </div>
 <p className="mt-3 text-white/70 max-w-2xl text-sm md:text-base leading-relaxed">
 {s.priceNote}
 </p>
 </div>
 <div className="md:col-span-5 md:justify-self-end flex flex-col gap-3">
 <div className="text-xs text-white/60 max-w-sm">{s.rebateSummary}</div>
 <Link
 to="/#contact"
 className="inline-flex items-center justify-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold px-6 py-4 text-sm transition-colors w-fit"
 >
 Book a Free Estimate <ArrowUpRight className="w-4 h-4" />
 </Link>
 </div>
 </div>
 </section>
 )}

 {/* Why choose ElectroMax */}
 <section className="py-24 lg:py-32 border-t border-[#262626]">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
 <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
 <div className="md:col-span-7">
 <div className="text-xs tracking-[0.24em] uppercase text-white/60 mb-4">{s.whyUsKicker || "— Why ElectroMax Systems"}</div>
 <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
 {s.whyUsHeadingA || "Six reasons customers"}
 <br />
 <span className="text-white/40">{s.whyUsHeadingB || "book us instead."}</span>
 </h2>
 </div>
 </div>
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
 {s.whyUs.map((w, i) => {
 const Icon = WHY_ICONS[i] || ShieldCheck;
 return (
 <div key={w.h} className="bg-[#0A0A0A] p-6 md:p-8">
 <div className="w-10 h-10 border border-[#262626] flex items-center justify-center">
 <Icon className="w-4 h-4 text-[#E60000]" strokeWidth={2} />
 </div>
 <h3 className="mt-6 font-display text-xl md:text-2xl font-bold leading-tight">{w.h}</h3>
 <p className="mt-3 text-sm text-white/70 leading-relaxed">{w.p}</p>
 </div>
 );
 })}
 </div>
 </div>
 </section>

 {/* Gallery */}
 <section className="py-24 lg:py-32 border-t border-[#262626] bg-[#0A0A0A]">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
 <div className="text-xs tracking-[0.24em] uppercase text-white/60 mb-4">— Recent Installs</div>
 <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95] mb-12">
 A few we&rsquo;ve wired up.
 </h2>
 <div className="grid md:grid-cols-3 gap-4 md:gap-6">
 {s.gallery.map((g, i) => (
 <div key={i} className="border border-[#262626] overflow-hidden group">
 <div className="aspect-[4/3] overflow-hidden">
 <img
 src={g.img}
 alt={g.cap}
 className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
 />
 </div>
 <div className="p-4 bg-[#141414] flex items-center justify-between">
 <span className="text-sm text-white/85">{g.cap}</span>
 <ArrowUpRight className="w-4 h-4 text-white/40 group-hover:text-[#E60000] transition-colors" />
 </div>
 </div>
 ))}
 </div>
 </div>
 </section>

 {/* CTA banner */}
 <section className="relative border-y border-[#E60000]/40 bg-[#E60000] overflow-hidden">
 <div className="absolute inset-0 opacity-20 mix-blend-overlay pointer-events-none">
 <div
 className="w-full h-full"
 style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(0,0,0,0.35) 0 2px, transparent 2px 14px)" }}
 />
 </div>
 <div className="relative mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-14 md:py-20 grid md:grid-cols-12 gap-8 items-center">
 <div className="md:col-span-8">
 <div className="text-xs tracking-[0.24em] uppercase text-black/70 font-bold mb-4">
 {s.ctaBannerKicker || "Ready to Get Your EV Charger Installed?"}
 </div>
 <h3 className="font-display text-3xl sm:text-4xl md:text-5xl tracking-tighter font-black leading-[0.95]">
 {s.ctaBannerHeadingA || "Call today for a free estimate."}
 <br className="hidden md:block" />{" "}
 <span className="text-black">{s.ctaBannerHeadingB || "Local, licensed, ready to install."}</span>
 </h3>
 </div>
 <div className="md:col-span-4 flex md:justify-end">
 <a
 href={BUSINESS.phoneHref}
 className="inline-flex items-center gap-3 bg-[#0A0A0A] text-white hover:bg-black transition-colors font-bold px-6 py-5 group"
 >
 <span className="relative inline-flex w-10 h-10 bg-[#E60000] items-center justify-center">
 <Phone className="w-4 h-4" strokeWidth={2.5} />
 <span className="absolute inset-0 border border-white/40 animate-ping" />
 </span>
 <span className="flex flex-col text-left">
 <span className="text-[10px] tracking-[0.24em] uppercase text-white/50">Call now</span>
 <span className="font-display text-xl md:text-2xl tracking-tight">
 {BUSINESS.phone}
 </span>
 </span>
 </a>
 </div>
 </div>
 </section>

 {/* FAQ */}
 <section className="py-24 lg:py-32 bg-[#F4F4F5] text-[#0A0A0A] border-t border-[#E4E4E7]">
 <div className="mx-auto max-w-[1000px] px-6 md:px-10 lg:px-16">
 <div className="text-xs tracking-[0.24em] uppercase text-black/50 mb-4">— Frequently Asked</div>
 <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
 {s.faqHeadingA || "Questions about EV"}
 <br />
 <span className="text-black/40">{s.faqHeadingB || "charger installation."}</span>
 </h2>
 <div className="mt-12 divide-y divide-[#E4E4E7] border-y border-[#E4E4E7]">
 {s.faqs.map((f, i) => (
 <details
 key={i}
 data-testid={`faq-item-${i}`}
 className="group py-6 [&_summary::-webkit-details-marker]:hidden"
 >
 <summary className="flex items-start justify-between gap-6 cursor-pointer list-none">
 <span className="font-display text-lg md:text-xl font-bold text-black">{f.q}</span>
 <span className="mt-1 shrink-0 inline-flex w-8 h-8 border border-[#E4E4E7] items-center justify-center group-open:bg-[#E60000] group-open:border-[#E60000] group-open:text-white transition-colors">
 <span className="block relative w-3 h-3">
 <span className="absolute inset-x-0 top-1/2 h-[2px] bg-current" />
 <span className="absolute inset-y-0 left-1/2 w-[2px] bg-current group-open:hidden" />
 </span>
 </span>
 </summary>
 <p className="mt-4 text-sm md:text-base text-black/70 leading-relaxed max-w-3xl">{f.a}</p>
 </details>
 ))}
 </div>
 </div>
 </section>

 {/* Serving these Lower Mainland cities */}
 {SERVICE_LOCAL[slug] && (
 <section className="py-24 lg:py-32 border-t border-[#262626] bg-[#0A0A0A]">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
 <div className="grid md:grid-cols-12 gap-8 items-end mb-10">
 <div className="md:col-span-7">
 <div className="text-xs tracking-[0.24em] uppercase text-[#E60000] font-bold mb-4">— City-Specific Pages</div>
 <h2 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold leading-[0.95]">
 Serving Metro Vancouver
 <br />
 <span className="text-white/40">& the Fraser Valley.</span>
 </h2>
 </div>
 <p className="md:col-span-5 text-white/70 leading-relaxed">
 {SERVICE_LOCAL[slug].blurb || `We install ${SERVICE_LOCAL[slug].label.toLowerCase()}s across the Lower Mainland. Every city has its own quirks — panel age, permit desk, strata rules. Pick your city for a page written specifically for you.`}
 </p>
 </div>
 <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4 md:gap-5">
 {SERVICE_LOCAL[slug].order.map((cs) => {
 const c = SERVICE_LOCAL[slug].cities[cs];
 return (
 <Link
 key={cs}
 to={`/services/${slug}/${cs}`}
 data-testid={`svc-city-link-${cs}`}
 className="group border border-[#262626] hover:border-[#E60000] p-6 flex flex-col gap-1 transition-colors"
 >
 <div className="text-[10px] tracking-[0.2em] uppercase text-[#E60000] font-bold">{SERVICE_LOCAL[slug].label}</div>
 <div className="mt-1 font-display text-2xl font-bold group-hover:text-white transition-colors">
 {c.city}
 </div>
 <div className="text-xs text-white/50 mt-1">Permit handled · Licensed</div>
 <ArrowUpRight className="w-4 h-4 mt-3 text-white/40 group-hover:text-[#E60000] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
 </Link>
 );
 })}
 </div>

 {SERVICE_LOCAL[slug].guideHref && (
 <div className="mt-10 border border-[#262626] bg-[#141414] p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
 <div>
 <div className="text-[10px] tracking-[0.24em] uppercase text-[#E60000] font-bold mb-2">Resource</div>
 <div className="font-display text-2xl md:text-3xl font-bold tracking-tight">
 {SERVICE_LOCAL[slug].guideTitle}
 </div>
 <p className="mt-2 text-white/70 max-w-2xl text-sm md:text-base">
 {SERVICE_LOCAL[slug].guideBody}
 </p>
 </div>
 <Link
 to={SERVICE_LOCAL[slug].guideHref}
 data-testid="svc-rebates-guide-cta"
 className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold px-6 py-4 text-sm transition-colors shrink-0"
 >
 Read the Guide <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
 </Link>
 </div>
 )}
 </div>
 </section>
 )}

 {/* Related services */}
 <section className="py-20 border-t border-[#262626]">
 <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16">
 <div className="flex items-end justify-between gap-6 mb-10 flex-wrap">
 <h2 className="font-display text-3xl md:text-4xl tracking-tighter font-bold">Other services</h2>
 <Link
 to="/#services"
 className="text-sm font-bold text-white/70 hover:text-white inline-flex items-center gap-1"
 >
 <ArrowLeft className="w-4 h-4" /> Back to all services
 </Link>
 </div>
 <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
 {SERVICES.filter((x) => x.id !== s.id).slice(0, 6).map((x) => (
 <Link
 key={x.id}
 to={`/services/${x.id}`}
 className="group border border-[#262626] hover:border-white/40 p-6 flex items-center justify-between transition-colors"
 >
 <div>
 <div className="text-[10px] tracking-[0.2em] uppercase text-[#E60000] font-bold">
 {x.tag}
 </div>
 <div className="mt-1 font-display text-xl font-bold">{x.title}</div>
 </div>
 <ArrowUpRight
 className="w-5 h-5 text-white/40 group-hover:text-[#E60000] transition-colors"
 strokeWidth={2.5}
 />
 </Link>
 ))}
 </div>
 </div>
 </section>
 </div>
 );
}
